import Vue from 'vue';
import Vuex, { Module } from 'vuex';
import axios from 'axios';

import { endpoints } from '@/endpoints/endpoints';
import { handleError } from '@/utils/helperFunctions';
import { EMAIL_EVENT_ACTIONS } from '@/constants/email-event-actions';
import { isHphCartBlocked } from '@/constants/hph-cart-gate';
import { isCartBlockedSession } from '@/utils/roles';

import {
  Basket,
  BasketDetails,
  Cart,
  Baskets,
  AddBasket,
  Product,
} from '@/definitions/definitions';

Vue.use(Vuex);

export const baskets: Module<Baskets, any> = {
  state: {
    buy: {
      open: false,
      edit: false,
      editDetails: null,
      details: null,
    },
    sell: {
      open: false,
      edit: false,
      editDetails: null,
      details: null,
    },
    cart: {
      open: false,
      fetching: false,
      items: [],
    },
    documents: null,
    tradeResolved: {},
    checkoutActive: false,
    openModal: false,
    hphCartDisabledNotice: false,
  },
  mutations: {
    toggleCart(state: Baskets, data: boolean) {
      state.cart.open = data;
    },
    toggleHphCartDisabledNotice(state: Baskets, data: boolean) {
      state.hphCartDisabledNotice = data;
    },
    setCart(state: Baskets, data: BasketDetails[]) {
      state.cart.items = data;
    },
    setFetching(state: Baskets, data: boolean) {
      state.cart.fetching = data;
    },
    toggleBuyBasket(state: Baskets, data: boolean) {
      state.buy.open = data;
    },
    toggleBasketModal(state: Baskets, data: boolean) {
      state.openModal = data;
    },
    buyBasketEdit(state: Baskets, data: boolean) {
      state.buy.edit = data;
    },
    buyBasketEditDetails(state: Baskets, data: boolean) {
      state.buy.editDetails = data;
    },
    sellBasketEditDetails(state: Baskets, data: boolean) {
      state.sell.editDetails = data;
    },
    toggleSellBasket(state: Baskets, data: boolean) {
      state.sell.open = data;
    },
    sellBasketEdit(state: Baskets, data: boolean) {
      state.sell.edit = data;
    },
    updateBuyBasketDetails(state: Baskets, data: BasketDetails) {
      state.buy.details = data;
    },
    updateSellBasketDetails(state: Baskets, data: BasketDetails) {
      state.sell.details = data;
    },
    updateDocuments(state: Baskets, data: any[] | null) {
      state.documents = data;
    },
    updateTradeResolved(state: Baskets, data: any) {
      state.tradeResolved = data;
    },
    setCheckoutActive(state: Baskets, data: boolean) {
      state.checkoutActive = data;
    },
  },
  actions: {
    // update product details for the buy basket modal
    updateBuyBasketDetails(state: Module<Baskets, any>, data: BasketDetails) {
      if (isCartBlockedSession(this.state.account?.details)) {
        this.dispatch(
          'addError',
          'Buy feature is not available, Please login as the client to continue with buy/sell process',
        );
        return;
      }
      const details: BasketDetails = {
        TransactionType: 'Purchase',
        TransactionId: 0,
        ProductType: data.ProductType,
        NoteID: null,
        FundId: null,
        Denomination: data.Denomination,
        MinInvest: data.MinInvest ? data.MinInvest : null,
        ISIN: data.ISIN,
        FundName: data.FundName,
        Coupon: data.Coupon,
        Currency: data.Currency,
        UserInfoId: null,
        CurrentUserBostonId: null,
        PlatformId: null,
        TradeOptions: data.TradeOptions,
        Units: data.TradeOptions === 'Units' ? data.Units : 0,
        Amount: data.TradeOptions === 'Amount' ? data.Amount : 0,
        PrcentageAvailableCash: 0,
        PrcentagePortfolio: 0,
        PrcentageHolding: 0,
        NAV: data.NAV,
        MovNav: data.MovNav ? data.MovNav : null,
      };
      if (data.MovNav && data.MovNav > 0) {
        details.NAV = data.MovNav;
      }
      if (data.Coupon) {
        details.Coupon = data.Coupon;
      }
      this.commit('updateBuyBasketDetails', details);
    },
    updateSellBasketDetails(state: Module<Baskets, any>, data: BasketDetails) {
      this.commit('updateSellBasketDetails', data);
    },
    async buyBasket(state: Module<Baskets, any>, data: AddBasket) {
      // check if consultant logged in as client
      if (data.CurrentUserBostonId && window.sessionStorage.getItem('consultantBostonID') && window.sessionStorage.getItem('consultantBostonID') !== '') {
        const CurrentUserBostonId: any = window.sessionStorage.getItem('consultantBostonID');
        data.CurrentUserBostonId = CurrentUserBostonId;
      }
      const res = await axios
        .post(endpoints.addCart, [data], {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          if (data.ProductType === 'Fixed Income Funds') {
            if (data.TransactionType === 'Sell') {
              this.commit('updateSnackbarMessage', 'Successfully added sell order to cart');
            } else {
              this.commit('updateSnackbarMessage', 'Successfully added to cart');
            }
            this.dispatch('getCart');
          } else if (data.ProductType === 'Hybrid, Structured Instruments') {
            if (response.data.Data.GeneratePDF) {
              this.commit('updateSnackbarMessage', 'Successfully generated Purchase Order documents');
            } else {
              this.commit('updateSnackbarMessage', 'Added ' + data.FundName + ' to your cart');
            }
            this.dispatch('getCart');

          } else {
            if (response.data.Data.GeneratePDF) {
              this.commit('updateDocuments', response.data.Data);
            }
            this.commit('updateSnackbarMessage', 'Added ' + data.FundName + ' to your cart');
            this.dispatch('getCart');
          }
          // remove buy modal details to close
          this.commit('toggleBuyBasket', false);
          this.commit('buyBasketEdit', false);
        })
        .catch((err) => {
          const msg = handleError(err, 'Sorry! looks like something went wrong while trying to purchase product');
          this.dispatch('addError', msg);
        });
    },
    async updateBasket(state: Module<Baskets, any>, data: AddBasket) {
      const res = await axios
        .post(endpoints.updateCart, data, {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        })
        .then((response) => {
          if (data.ProductType === 'Fixed Income Funds') {
            if (data.TransactionType === 'Sell') {
              this.commit('updateSnackbarMessage', 'Successfully updated sell order to cart');
            } else {
              this.commit('updateSnackbarMessage', 'Successfully updated ');
            }
            this.dispatch('getCart');
          } else if (data.ProductType === 'Hybrid, Structured Instruments') {
            if (response.data.Data.GeneratePDF) {
              this.commit('updateSnackbarMessage', 'Successfully updated Purchase Order ');
            } else {
              this.commit('updateSnackbarMessage', 'Updated cart item details');
            }
            this.dispatch('getCart');

          } else {
            if (response.data.Data.GeneratePDF) {
              this.commit('updateDocuments', response.data.Data);
            }
            this.commit('updateSnackbarMessage', 'Updated cart item details' );
            this.dispatch('getCart');
          }
          // remove buy modal details to close
          this.commit('toggleBuyBasket', false);
          this.dispatch('toggleCart', true);
          this.commit('buyBasketEdit', false);
        })
        .catch((err) => {
          const msg = handleError(err, 'Sorry! looks like something went wrong while trying to update product details');
          this.dispatch('addError', msg);
        });
    },

    async deleteCart(state: Module<Baskets, any>, data: any) {
      const res = await axios
        .post(
          endpoints.deleteCart,
         data,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          if (!data.disableSnackbar) {
            this.commit('updateSnackbarMessage', 'Removed ' + data.name + ' from your cart');
          }
          this.dispatch('getCart');
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(err, 'Sorry! looks like something went wrong while trying to delete the item');
          this.dispatch('addError', msg);
        });
    },

    async getCart(state: any, data: any) {
      this.commit('setFetching', true);
      const res = await axios
        .get(
          endpoints.cart +
            '?whitelabelID=' +
            parseInt(this.state.account.details.whiteLabelId, 10) +
            '&userInfoID=' +
            this.state.account.details.userId +
            '&accountId=' +
            this.state.account.details.activeAccount.id,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then((response) => {
          if (response.data.Data.CartInformation) {
            this.commit('setCart', response.data.Data);
            this.commit('setFetching', false);
          } else {
            this.commit('setCart', []);
            this.commit('setFetching', false);
          }
          return response.data;
        })
        .catch((err) => {
          const msg = handleError(err, 'Sorry! looks like something went wrong while getting your cart');
          // throw console error here rather than a hard error modal as we display the cart with error
          this.dispatch('addError', msg);
          console.error(msg);
          this.commit('setFetching', false);
          this.commit('setCart', []);
          return err;
        });
    },
    toggleBuyBasket(state: any, data: boolean) {
      if (data === true && isCartBlockedSession(this.state.account?.details)) {
        this.dispatch(
          'addError',
          'Buy feature is not available, Please login as the client to continue with buy/sell process',
        );
        return;
      }
      this.commit('toggleBuyBasket', data);
      this.commit('updateDocuments', null);
    },
    toggleBasketModal(state: any, data: boolean) {
      this.commit('toggleBasketModal', data);
    },
    buyBasketEdit(state: any, data: boolean) {
      this.commit('buyBasketEdit', data);
    },
    toggleSellBasket(state: any, data: boolean) {
      this.commit('toggleSellBasket', data);
    },
    sellBasketEdit(state: any, data: boolean) {
      this.commit('sellBasketEdit', data);
    },
    toggleCart(state: any, data: boolean) {
      // Opening the cart: HPH clients see a support notice instead (see hph-cart-gate).
      if (data === true) {
        const account = this.state.account;
        const blocked = isHphCartBlocked({
          username: account?.username,
          companyCode: account?.details?.companyCode,
          whitelabelCode: this.state.subdomain,
        });
        if (blocked) {
          this.commit('toggleCart', false);
          this.commit('toggleHphCartDisabledNotice', true);
          // Fire-and-forget internal email event (server dedupes + owns recipients)
          void this.dispatch('notifyCartAccessBlocked');
          return;
        }
      }
      this.commit('toggleCart', data);
    },
    async notifyCartAccessBlocked() {
      try {
        const account = this.state.account;
        const details = account?.details;
        await axios.post(
          endpoints.emailEvents,
          {
            actionType: EMAIL_EVENT_ACTIONS.CART_ACCESS_BLOCKED,
            context: {
              companyCode: details?.companyCode ?? this.state.subdomain ?? '',
              username: account?.username ?? '',
              // AccountNo (Moventum VendorId) is resolved server-side from bostonId / username.
              accountNo: '',
              bostonId: details?.bostonId ?? null,
              occurredAt: new Date().toISOString(),
            },
          },
          {
            headers: {
              Authorization: 'bearer ' + account?.token,
            },
          },
        );
      } catch (err) {
        // Do not block the modal UX if notification fails
        console.warn('notifyCartAccessBlocked failed', err);
      }
    },
    toggleHphCartDisabledNotice(state: any, data: boolean) {
      this.commit('toggleHphCartDisabledNotice', data);
    },

    async placeSellOrder(state: Module<Baskets, any>, data: any ) {
      const res = await axios
      .post(
        endpoints.processCart,
       data,
        {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        },
      )
      .then((response) => {
        this.commit('updateTradeResolved', response.data);
        this.dispatch('getCart');
        return response.data;
      })
      .catch((err) => {
        const msg = handleError(err, 'Sorry! looks like something went wrong while trying to checkout');
        this.dispatch('addError', msg);
        this.commit('updateTradeResolved', err);
      });
    },
    async placeBuyOrder(state: Module<Baskets, any>, data: any ) {
      const res = await axios
      .post(
        endpoints.processCart,
       data,
        {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        },
      )
      .then((response) => {
        this.commit('updateDocuments', response.data.Data);
        this.commit('updateTradeResolved', response.data);
        this.dispatch('getCart');
        return response.data;
      })
      .catch((err) => {
        const msg = handleError(err, 'Sorry! Looks like something went wrong while trying to process your request. Our support team has been notified of this error.');
        this.dispatch('addError', msg);
        this.commit('updateTradeResolved', err);
      });
    },
    async startDocusignProcess(state: Module<Baskets, any>) {

      const res = await axios
      .post(
        endpoints.docusignCreate,
        {
          SignerDetails: this.state.baskets.documents.PDFInformation.SignerDetails,
          PDFFileDetails: this.state.baskets.documents.PDFInformation.PDFUrls,
        },
        {
          headers: {
            Authorization: 'bearer ' + this.state.account.token,
          },
        },
      )
      .then((response) => {
        this.commit('updateSnackbarMessage', 'Your Docusign emails have been sent. Please check your emails');
        this.commit('toggleBuyBasket', false);
        return response.data;
      })
      .catch((err) => {
        const msg = handleError(err, 'Sorry! Looks like something went wrong while generating Docusign documents.');
        this.dispatch('addError', msg);
      });
    },
  },
};
